import { useMemo, useState } from "react";
import type {
  CoachEngagementFilters,
  EngagementListItem,
  EngagementMatchRow,
  EngagementPlayerRow,
  EngagementPlaylistRow,
  EngagementReflectionItem,
} from "@/lib/types";
import { useMatches } from "@/features/matches/useMatches";
import { useRoster } from "./hooks/useRoster";
import { useCoachPlaylistsList } from "./hooks/useCoachPlaylistsList";
import { useCoachEngagement } from "./hooks/useCoachEngagement";
import { PageHeader } from "./components/PageHeader";
import { TkticDateInput } from "@/components/ui/TkticDateInput";

/**
 * Coach > Engagement dashboard (M6a).
 *
 * Ports the legacy `renderCoachEngagementDashboard`
 * (`js/coaching/engagement.js`): a filter bar + five KPI tiles +
 * three breakdown tables (by player / playlist / match) + four
 * follow-up lists (unreviewed assigned items, reflections needing
 * response, players with no recent feedback, most watched).
 *
 * Filter-driven: the filter bar's six controls (player · playlist
 * · match · visibility · start date · end date) feed a `filters`
 * object that's the query key for `useCoachEngagement` — changing
 * a filter refetches.
 *
 * The dashboard's `limitations` field is surfaced as caveat notes
 * (e.g. "clip watch tracking is not yet supported") so the coach
 * knows what the numbers do and don't cover.
 */

const VISIBILITY_FILTER_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "", label: "All visibilities" },
  { value: "player", label: "Player + family" },
  { value: "team", label: "Team" },
];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function EngagementPage() {
  // ---- Filter state ----
  // Held as raw strings (the controls' values); converted to the
  // typed `CoachEngagementFilters` for the query. An invalid date
  // is simply not forwarded — the query ignores it rather than
  // 422-ing.
  const [playerId, setPlayerId] = useState("");
  const [playlistId, setPlaylistId] = useState("");
  const [matchId, setMatchId] = useState("");
  const [visibility, setVisibility] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const filters: CoachEngagementFilters = useMemo(() => {
    const f: CoachEngagementFilters = {};
    if (playerId) f.player_id = playerId;
    if (playlistId) f.playlist_id = Number(playlistId);
    if (matchId) f.match_id = matchId;
    if (visibility) f.visibility = visibility;
    if (startDate && DATE_RE.test(startDate)) f.start_date = startDate;
    if (endDate && DATE_RE.test(endDate)) f.end_date = endDate;
    return f;
  }, [playerId, playlistId, matchId, visibility, startDate, endDate]);

  // Filter-option sources.
  const rosterQuery = useRoster();
  const playlistsQuery = useCoachPlaylistsList();
  const matchesQuery = useMatches();
  const players = rosterQuery.data?.players ?? [];
  const playlists = playlistsQuery.data?.playlists ?? [];
  const matches = matchesQuery.data ?? [];

  const engagementQuery = useCoachEngagement(filters);
  const data = engagementQuery.data;

  return (
    <div className="flex flex-col gap-5" data-testid="engagement-page">
      <PageHeader
        title="Review Completion & Engagement"
        description="Coach/admin-only aggregates from review completions and player reflections. Private note bodies and coach-only notes are not shown here."
        actions={
          <button
            type="button"
            onClick={() => engagementQuery.refetch()}
            disabled={engagementQuery.isFetching}
            data-testid="engagement-refresh"
            className="border border-rule text-ink hover:bg-surface disabled:opacity-50 rounded px-4 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
          >
            {engagementQuery.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        }
      />

      {/* Filter bar — six controls. */}
      <div
        className="flex flex-wrap gap-3 rounded-lg border border-rule bg-bg-card p-3"
        data-testid="engagement-filters"
      >
        <FilterSelect
          label="Player"
          value={playerId}
          onChange={setPlayerId}
          testid="engagement-filter-player"
        >
          <option value="">All players</option>
          {players.map((p) => (
            <option key={p.id} value={p.id}>
              {p.jersey_number
                ? `#${p.jersey_number} ${p.display_name}`
                : p.display_name}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          label="Playlist"
          value={playlistId}
          onChange={setPlaylistId}
          testid="engagement-filter-playlist"
        >
          <option value="">All playlists</option>
          {playlists.map((pl) => (
            <option key={pl.id} value={String(pl.id)}>
              {pl.title || `Playlist ${pl.id}`}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          label="Match"
          value={matchId}
          onChange={setMatchId}
          testid="engagement-filter-match"
        >
          <option value="">All matches</option>
          {matches.map((m) => (
            <option key={m.id} value={m.id}>
              {m.home_team} vs {m.away_team}
              {m.date ? ` · ${m.date}` : ""}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          label="Visibility"
          value={visibility}
          onChange={setVisibility}
          testid="engagement-filter-visibility"
        >
          {VISIBILITY_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FilterSelect>
        <FilterDate
          label="Start date"
          value={startDate}
          onChange={setStartDate}
          testid="engagement-filter-start"
        />
        <FilterDate
          label="End date"
          value={endDate}
          onChange={setEndDate}
          testid="engagement-filter-end"
        />
      </div>

      {engagementQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="engagement-loading"
        >
          Loading engagement dashboard…
        </p>
      ) : engagementQuery.isError || !data ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="engagement-error"
        >
          Could not load the engagement dashboard.
        </p>
      ) : (
        <>
          {/* KPI tiles. */}
          <div
            className="grid grid-cols-2 gap-3 lg:grid-cols-5"
            data-testid="engagement-kpis"
          >
            <KpiTile
              label="Assigned items"
              value={String(data.summary.assigned_items)}
            />
            <KpiTile
              label="Reviewed items"
              value={String(data.summary.reviewed_items)}
            />
            <KpiTile
              label="Completion"
              value={`${data.summary.completion_percentage}%`}
            />
            <KpiTile
              label="Reflections"
              value={String(data.summary.reflection_count)}
            />
            <KpiTile
              label="Latest review"
              value={data.summary.latest_reviewed_at ?? "—"}
            />
          </div>

          {/* Caveats — the dashboard's `limitations`. */}
          {data.limitations &&
          Object.keys(data.limitations).length > 0 ? (
            <p
              className="text-ink-muted text-xs"
              data-testid="engagement-limitations"
            >
              {Object.values(data.limitations)
                .filter((v) => typeof v === "string")
                .join(" · ")}
            </p>
          ) : null}

          {/* Breakdown tables. */}
          <PlayerBreakdownTable rows={data.by_player} />
          <PlaylistBreakdownTable rows={data.by_playlist} />
          <MatchBreakdownTable rows={data.by_match} />

          {/* Follow-up lists. */}
          <div className="grid gap-3 lg:grid-cols-2">
            <FollowUpList
              title="Unreviewed assigned items"
              testid="engagement-unreviewed"
              emptyLabel="No unreviewed assigned items for these filters."
              items={data.unreviewed_assigned_items.map((i) => ({
                key: `${i.kind}-${i.item_id}`,
                primary: i.title || `${i.kind} ${i.item_id}`,
                meta: `${i.kind}${i.date ? ` · ${i.date}` : ""}`,
              }))}
            />
            <FollowUpList
              title="Reflections needing response"
              testid="engagement-reflections"
              emptyLabel="No player reflections for these filters."
              items={data.reflections_needing_response.map(
                (r: EngagementReflectionItem, idx) => ({
                  key: `reflection-${idx}`,
                  primary: r.reviewed_at ?? "Reflection",
                  meta: r.reflection,
                }),
              )}
            />
            <FollowUpList
              title="Players with no recent feedback"
              testid="engagement-no-recent"
              emptyLabel="Every active filtered player has recent feedback."
              items={data.players_with_no_recent_feedback.map((p) => ({
                key: p.player_id,
                primary: p.jersey_number
                  ? `#${p.jersey_number} ${p.display_name}`
                  : p.display_name,
                meta: "No feedback in the recent window.",
              }))}
            />
            <FollowUpList
              title="Most watched"
              testid="engagement-most-watched"
              emptyLabel="No review activity yet."
              items={data.most_watched.map((i: EngagementListItem) => ({
                key: `${i.kind}-${i.item_id}`,
                primary: i.title || `${i.kind} ${i.item_id}`,
                meta: `${i.review_count ?? 0} review${
                  (i.review_count ?? 0) === 1 ? "" : "s"
                } · ${i.kind}`,
              }))}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ---- Filter controls ----

function FilterSelect(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        data-testid={props.testid}
        className="tktic-select"
      >
        {props.children}
      </select>
    </label>
  );
}

function FilterDate(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      <TkticDateInput
        value={props.value}
        onChange={props.onChange}
        data-testid={props.testid}
      />
    </label>
  );
}

// ---- KPI tile ----

function KpiTile(props: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-rule bg-bg-card px-3 py-2">
      <div className="text-ink-muted text-[0.65rem] font-display uppercase tracking-wider">
        {props.label}
      </div>
      <strong className="mt-0.5 block text-xl font-display text-ink tabular-nums">
        {props.value}
      </strong>
    </div>
  );
}

// ---- Breakdown tables ----

function BreakdownCard(props: {
  title: string;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="rounded-lg border border-rule bg-bg-card p-3"
      data-testid={props.testid}
    >
      <h3 className="text-sm font-display text-ink mb-2">
        {props.title}
      </h3>
      <div className="overflow-x-auto">{props.children}</div>
    </section>
  );
}

const TH = "px-2 py-1 text-left text-xs font-display uppercase tracking-wider text-ink-muted";
const TD = "px-2 py-1 text-sm text-ink tabular-nums";

function PlayerBreakdownTable({ rows }: { rows: EngagementPlayerRow[] }) {
  return (
    <BreakdownCard
      title="Review completion by player"
      testid="engagement-by-player"
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-rule">
            <th className={TH}>Player</th>
            <th className={TH}>Assigned</th>
            <th className={TH}>Reviewed</th>
            <th className={TH}>Complete</th>
            <th className={TH}>Latest</th>
            <th className={TH}>Reflections</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={TD} colSpan={6}>
                No assigned feedback matches these filters.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.player_id}
                className="border-b border-rule/50"
              >
                <td className={`${TD} font-display`}>
                  {r.jersey_number
                    ? `#${r.jersey_number} ${r.display_name}`
                    : r.display_name}
                </td>
                <td className={TD}>{r.assigned_count}</td>
                <td className={TD}>{r.reviewed_count}</td>
                <td className={TD}>{r.completion_percentage}%</td>
                <td className={TD}>{r.latest_reviewed_at ?? "—"}</td>
                <td className={TD}>{r.reflection_count}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </BreakdownCard>
  );
}

function PlaylistBreakdownTable({
  rows,
}: {
  rows: EngagementPlaylistRow[];
}) {
  return (
    <BreakdownCard
      title="Review completion by playlist"
      testid="engagement-by-playlist"
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-rule">
            <th className={TH}>Playlist</th>
            <th className={TH}>Assigned</th>
            <th className={TH}>Reviewed</th>
            <th className={TH}>Complete</th>
            <th className={TH}>Latest</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={TD} colSpan={5}>
                No playlists match these filters.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.playlist_id}
                className="border-b border-rule/50"
              >
                <td className={`${TD} font-display`}>
                  {r.title || `Playlist ${r.playlist_id}`}
                </td>
                <td className={TD}>{r.assigned_count}</td>
                <td className={TD}>{r.reviewed_count}</td>
                <td className={TD}>{r.completion_percentage}%</td>
                <td className={TD}>{r.latest_reviewed_at ?? "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </BreakdownCard>
  );
}

function MatchBreakdownTable({ rows }: { rows: EngagementMatchRow[] }) {
  return (
    <BreakdownCard
      title="Review completion by match"
      testid="engagement-by-match"
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-rule">
            <th className={TH}>Match</th>
            <th className={TH}>Date</th>
            <th className={TH}>Assigned</th>
            <th className={TH}>Reviewed</th>
            <th className={TH}>Complete</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={TD} colSpan={5}>
                No matches have assigned feedback for these filters.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.match_id}
                className="border-b border-rule/50"
              >
                <td className={`${TD} font-display`}>
                  {r.label || r.match_id}
                </td>
                <td className={TD}>{r.date || "—"}</td>
                <td className={TD}>{r.assigned_count}</td>
                <td className={TD}>{r.reviewed_count}</td>
                <td className={TD}>{r.completion_percentage}%</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </BreakdownCard>
  );
}

// ---- Follow-up lists ----

function FollowUpList(props: {
  title: string;
  testid: string;
  emptyLabel: string;
  items: ReadonlyArray<{ key: string; primary: string; meta: string }>;
}) {
  return (
    <section
      className="rounded-lg border border-rule bg-bg-card p-3"
      data-testid={props.testid}
    >
      <h3 className="text-sm font-display text-ink mb-2">
        {props.title}
      </h3>
      {props.items.length === 0 ? (
        <p className="text-ink-muted text-xs">{props.emptyLabel}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {props.items.map((item) => (
            <li
              key={item.key}
              className="flex flex-col gap-0.5 rounded border border-rule/60 bg-surface px-2 py-1.5"
            >
              <span className="text-sm text-ink truncate">
                {item.primary}
              </span>
              <span className="text-ink-muted text-xs truncate">
                {item.meta}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
